
import React from "react";

const OMNISCAN_BLUE = "#20AFE8";
const OMNISCAN_BLUE_RGB = "32, 175, 232";

const COLOURS = {
    service: OMNISCAN_BLUE,
    inspection: "#2ecc71",
    fault: "#d62828"
};

export default function AssetMaintenanceDrawer({
    onSelectInspection
}) {
    //----------------------------------------------------
    // Maintenance Events
    //----------------------------------------------------

    const events = [
        {
            day: 5,
            type: "service",
            title: "Routine Service"
        },
        {
            day: 14,
            type: "inspection",
            title: "Inspection Due"
        },
        {
            day: 22,
            type: "fault",
            title: "Display Fault"
        },
        {
            day: 28,
            type: "service",
            title: "Firmware Update"
        }
    ];

    //----------------------------------------------------
    // Helpers
    //----------------------------------------------------

    function getEvent(day) {
        return events.find(e => e.day === day);
    }

    function getColour(type) {
        return COLOURS[type] || OMNISCAN_BLUE;
    }

    const days = Array.from(
        { length: 31 },
        (_, i) => i + 1
    );

    //----------------------------------------------------
    // Render
    //----------------------------------------------------

    return (
        <div
            style={{
                padding: "28px",
                background:
                    "linear-gradient(180deg, rgba(18,34,49,0.10) 0%, rgba(9,20,33,0.18) 100%)",
                color: "#fff",
                height: "100%",
                overflowY: "auto",
                boxSizing: "border-box"
            }}
        >
            {/* TITLE */}

            <div
                style={{
                    color: "#ffffff",
                    fontSize: "28px",
                    fontWeight: 700,
                    letterSpacing: "-0.5px",
                    lineHeight: 1.2,
                    textShadow:
                        "0 2px 8px rgba(0,0,0,0.15)"
                }}
            >
                Maintenance Schedule
            </div>

            <div
                style={{
                    marginTop: "8px",
                    color: "#B9D1E2",
                    fontSize: "18px",
                    fontWeight: 400
                }}
            >
                January 2026
            </div>

            {/* SUBTLE ACCENT LINE */}

            <div
                style={{
                    marginTop: "20px",
                    height: "1px",
                    background:
                        `linear-gradient(90deg, rgba(${OMNISCAN_BLUE_RGB},0.45), rgba(255,255,255,0.04))`
                }}
            />

            {/* CALENDAR */}

            <div
                style={{
                    marginTop: "26px",
                    display: "grid",
                    gridTemplateColumns:
                        "repeat(7, minmax(0, 1fr))",
                    gap: "9px"
                }}
            >
                {days.map(day => {
                    const event = getEvent(day);

                    const eventColour = event
                        ? getColour(event.type)
                        : null;

                    return (
                        <div
                            key={day}
                            title={
                                event
                                    ? event.title
                                    : ""
                            }
                            role={
                                event
                                    ? "button"
                                    : undefined
                            }
                            tabIndex={
                                event
                                    ? 0
                                    : undefined
                            }
                            onClick={() => {
                                if (
                                    event &&
                                    onSelectInspection
                                ) {
                                    onSelectInspection(event);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (
                                    event &&
                                    onSelectInspection &&
                                    (
                                        e.key === "Enter" ||
                                        e.key === " "
                                    )
                                ) {
                                    e.preventDefault();
                                    onSelectInspection(event);
                                }
                            }}
                            style={{
                                height: "38px",

                                borderRadius: "4px",

                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",

                                cursor: event
                                    ? "pointer"
                                    : "default",

                                background: event
                                    ? eventColour
                                    : "rgba(255,255,255,0.075)",

                                border: event
                                    ? "1px solid rgba(255,255,255,0.20)"
                                    : "1px solid rgba(255,255,255,0.10)",

                                color: event
                                    ? "#ffffff"
                                    : "#D7E3EC",

                                fontSize: "13px",
                                fontWeight: 600,

                                boxShadow: event
                                    ? `0 5px 16px rgba(0,0,0,0.20), 0 0 12px ${eventColour}33`
                                    : "0 2px 8px rgba(0,0,0,0.08)",

                                backdropFilter:
                                    "blur(8px)",

                                WebkitBackdropFilter:
                                    "blur(8px)",

                                transition:
                                    "background 0.2s ease, transform 0.2s ease"
                            }}
                        >
                            {day}
                        </div>
                    );
                })}
            </div>

            {/* LEGEND */}

            <div
                style={{
                    marginTop: "32px",

                    borderRadius: "10px",

                    background:
                        "linear-gradient(145deg, rgba(255,255,255,0.10), rgba(255,255,255,0.045))",

                    border:
                        "1px solid rgba(255,255,255,0.13)",

                    boxShadow:
                        "0 10px 28px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.06)",

                    backdropFilter:
                        "blur(12px)",

                    WebkitBackdropFilter:
                        "blur(12px)",

                    padding: "20px"
                }}
            >
                <div
                    style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        letterSpacing: "1.4px",
                        textTransform: "uppercase",
                        color: "#8DC8F0",
                        marginBottom: "20px"
                    }}
                >
                    Maintenance Events
                </div>

                <Legend
                    colour={COLOURS.service}
                    text="Routine Service"
                />

                <Legend
                    colour={COLOURS.inspection}
                    text="Inspection"
                />

                <Legend
                    colour={COLOURS.fault}
                    text="Fault / Repair"
                    last
                />
            </div>
        </div>
    );
}

//========================================================
// Legend
//========================================================

function Legend({
    colour,
    text,
    last = false
}) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",

                marginBottom: last
                    ? "0px"
                    : "16px"
            }}
        >
            <div
                style={{
                    width: "12px",
                    height: "12px",

                    flexShrink: 0,
                    borderRadius: "50%",

                    background: colour,

                    boxShadow:
                        `0 0 10px ${colour}55`,

                    marginRight: "14px"
                }}
            />

            <div
                style={{
                    color: "#E2EBF2",
                    fontSize: "14px",
                    fontWeight: 500
                }}
            >
                {text}
            </div>
        </div>
    );
}
